
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Database,
  Settings,
  Menu,
  X,
  FolderOpen,
  FileText,
  Users,
  Phone,
  Building,
  Instagram,
  Shield,
  Sparkles
} from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Check if path is active
  const isActive = (path: string) => location.pathname === path;
  
  // Handle scroll for sticky header effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Navigation links configuration
  const navLinks = [
    { label: 'Dashboard', path: '/', icon: <LayoutDashboard size={18} /> },
    { label: 'NHF Codes', path: '/nhf-codes', icon: <Database size={18} /> },
    { label: 'Projects', path: '/projects', icon: <FolderOpen size={18} /> },
    { label: 'Services', path: '/services', icon: <Building size={18} /> },
    { label: 'Team', path: '/team', icon: <Users size={18} /> },
    { label: 'About Us', path: '/about', icon: <Users size={18} /> },
    { label: 'Contact', path: '/contact', icon: <Phone size={18} /> },
    { label: 'Case Study', path: '/case-study', icon: <FileText size={18} /> },
    { label: 'Settings', path: '/settings', icon: <Shield size={18} /> }
  ];
  
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/80 backdrop-blur-sm' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="h-10 relative flex items-center">
              <div className="flex items-center">
                <div className="bg-[#F97316] text-white p-2 rounded-md mr-2 shadow-lg shadow-orange-500/30 flex items-center justify-center">
                  <Sparkles size={20} className="mr-1" />
                  <span className="font-bold text-sm tracking-wider">NOVA</span>
                </div>
                <div className="h-8 w-1 bg-[#F97316] transform skew-x-12 -ml-1 shadow-lg shadow-orange-500/20"></div>
                <span className="font-bold text-base ml-3 text-white tracking-wider">IMPERIUM</span>
              </div>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 flex-wrap">
            {navLinks.map((link, index) => (
              <Link
                key={index}
                to={link.path}
                className={`flex items-center text-sm font-medium px-2 py-2 rounded-md transition-colors ${
                  isActive(link.path)
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="mr-1">{link.icon}</span>
                {link.label}
              </Link>
            ))}
            <a
              href="https://www.instagram.com/imperiumengineeringltd?igsh=bTVobWMyZnB0OGt1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm font-medium px-3 py-2 rounded-md transition-colors text-gray-300 hover:text-white hover:bg-white/5"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
          </nav>
          
          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-sm">
          <nav className="container mx-auto px-6 py-4">
            <div className="flex flex-col space-y-3">
              {navLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.path}
                  className={`flex items-center text-sm font-medium px-3 py-3 rounded-md transition-colors ${
                    isActive(link.path)
                      ? 'text-white bg-white/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="mr-2">{link.icon}</span>
                  {link.label}
                </Link>
              ))}
              <a
                href="https://www.instagram.com/imperiumengineeringltd?igsh=bTVobWMyZnB0OGt1"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-sm font-medium px-3 py-3 rounded-md transition-colors text-gray-300 hover:text-white hover:bg-white/5"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="mr-2"><Instagram size={18} /></span>
                Instagram
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
